<i18n
  src="@/locales/views/agro_exporter/finances/container_outcomes/show_org_invoice.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/container.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice_item.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice_payment.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice_discount.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_container')"
    :params="{
      agro_exporter_id: agro_exporter_id,
      agro_exporter_process_id: agro_exporter_process_id,
      container_id: container_id || org_invoice.container_id
    }"
  >
  </go-back-link>

  <bread-crumbs :labels="[module_name, agro_exporter_process.name]" />

  <template v-if="load_data_general">
    <div class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>
        <div class="panel-body">
          <div class="space-y-8 divide-y divide-slate-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.container") }}
                </dt>
                <dd class="content-data">
                  {{ org_invoice.container.container_code }}
                  <yes-no-label
                    :value="org_invoice.container.org_invoices_paid"
                    yes="Pagado"
                    no="No pagado"
                  />
                </dd>
              </div>
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.container.customer") }}
                </dt>
                <dd class="content-data">
                  {{ org_invoice.container.customer.name }}
                </dd>
              </div>

              <div class="sm:col-span-6" v-if="org_invoice.invoice_issuer">
                <dt class="label-data">
                  {{ $t("model.org_invoice.invoice_issuer") }}
                </dt>
                <dd class="content-data">
                  {{
                    org_invoice.invoice_issuer?.fullname ||
                    org_invoice.invoice_issuer?.name
                  }}
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.invoice_code") }}
                </dt>
                <dd class="content-data">
                  {{ org_invoice.invoice_code }}
                  <yes-no-label
                    class="mr-1"
                    :value="org_invoice.paid"
                    yes="Pagada"
                    no="No pagada"
                  />
                  <yes-no-label
                    class="mr-1"
                    v-if="org_invoice.fairtrade"
                    :value="org_invoice.fairtrade"
                    :yes="$t('model.org_invoice.fairtrade')"
                  />
                  <yes-no-label
                    class="mr-1"
                    v-if="org_invoice.fairtrade_incentive"
                    :value="org_invoice.fairtrade_incentive"
                    :yes="$t('model.org_invoice.fairtrade_incentive')"
                  />
                </dd>
              </div>
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.invoice_date") }}
                </dt>
                <dd class="content-data">
                  {{ dateFormatter(org_invoice.invoice_date) }}
                </dd>
              </div>
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.receipt_date") }}
                </dt>
                <dd class="content-data">
                  {{ dateFormatter(org_invoice.receipt_date) }}
                </dd>
              </div>
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.amount") }}
                </dt>
                <dd class="content-data">
                  {{
                    currencyMoney(
                      org_invoice.amount,
                      org_invoice.currency.symbol
                    )
                  }}
                </dd>
              </div>
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.scheduled_payment_at") }}
                </dt>
                <dd class="content-data">
                  {{ dateFormatter(org_invoice.scheduled_payment_at) }}
                </dd>
              </div>
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.amount_paid") }}
                </dt>
                <dd class="content-data">
                  {{
                    currencyMoney(
                      org_invoice.amount_paid,
                      org_invoice.currency.symbol
                    )
                  }}
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.detraction_total") }}
                </dt>
                <dd class="content-data">
                  {{
                    currencyMoney(
                      org_invoice.detraction_total,
                      org_invoice.currency.symbol
                    )
                  }}
                  ({{
                    numberWithPrecision(
                      Number(org_invoice.detraction_percent) * 100,
                      2
                    )
                  }}%)
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.org_invoice.detraction_total") }} (S/.)
                </dt>
                <dd class="content-data">
                  {{
                    org_invoice.exchange_rate
                      ? currencyMoney(
                          numberWithPrecision(
                            Number(org_invoice.detraction_total || 0) *
                              Number(org_invoice.exchange_rate),
                            2
                          ),
                          "S/."
                        ) +
                        "(TC: " +
                        org_invoice.exchange_rate +
                        ")"
                      : ""
                  }}
                </dd>
              </div>
            </div>
          </div>
        </div>
        <div class="panel-footer">
          <div class="panel-footer-actions">
            <destroy-button
              v-if="!proc_button_destroy"
              :disabled_button="false"
              @destroyEvent="destroyExportInvoice()"
              :label="$t('labels.destroy_button_label')"
              :title="
                $t('labels.destroy_button_title', {
                  invoice_code: org_invoice.invoice_code
                })
              "
              :message="$t('labels.destroy_button_message')"
              :label_confirm="$t('labels.destroy_button_label_confirm')"
            />
            <destroy-button-processing
              v-else
              :label="$t('labels.destroy_button_label')"
            />
            <span class="space-actions"></span>
            <pay-org-invoice-button
              :paid="org_invoice.paid"
              @payExportInvoice="updateExportInvoice"
            ></pay-org-invoice-button>
            <span class="space-actions"></span>
            <edit-button
              @click.prevent="goToEditExportInvoice()"
              :label="$t('labels.edit_button')"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="mt-8 flex flex-row">
      <h1 class="page-title">
        <i class="fa-solid fa-rectangle-list"></i>
        <span class="space-icon"></span>
        {{ $t("labels.title_org_invoice_items") }}
      </h1>
    </div>
    <div class="mt-8 flex flex-col">
      <div class="table-first-container">
        <div class="table-second-container">
          <div class="table-third-container">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_item.quantity") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_item.exporter_process_crop_package") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_item.description") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_item.unit_price") }}
                  </th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr
                  class="table-row"
                  v-for="(invoice_item, idx) in org_invoice.invoice_items"
                  :key="idx"
                >
                  <td class="table-cell">
                    {{ invoice_item.quantity }}
                  </td>
                  <td class="table-cell">
                    {{ invoice_item.exporter_process_crop_package?.label }}
                  </td>
                  <td class="table-cell">
                    {{ invoice_item.description }}
                  </td>
                  <td class="table-cell">
                    {{
                      currencyMoney(
                        invoice_item.unit_price,
                        org_invoice.currency.symbol
                      )
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-8 flex flex-row">
      <h1 class="page-title">
        <i class="fa-solid fa-money-check-dollar"></i>
        <span class="space-icon"></span>
        {{ $t("labels.title_org_invoice_payments") }}
      </h1>
    </div>
    <div class="mt-8 flex flex-row justify-between">
      <div class="panel-plain-label">
        <badge-default
          :label="
            org_invoice_payments.length +
            ' ' +
            $t('labels.total_number_org_invoice_payments')
          "
        />
        <span class="space-actions"></span>
        <badge-default
          :label="
            org_invoice.currency.symbol +
            numberWithPrecision(total_amount_paid) +
            ' ' +
            org_invoice.currency.plural_name
          "
        />
      </div>
      <new-button
        @click.prevent="goToNewInvoicePayment()"
        :label="$t('labels.new_org_invoice_payment_button')"
      />
    </div>
    <div class="mt-8 flex flex-col">
      <div class="table-first-container">
        <div class="table-second-container">
          <div class="table-third-container">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_payment.payment_at") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_payment.amount") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_payment.exchange_rate") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_payment.interbank_commission") }}
                  </th>
                  <th scope="col" class="table-header-cell"></th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr
                  class="table-row"
                  v-for="org_invoice_payment in org_invoice_payments"
                  :key="org_invoice_payment.id"
                >
                  <td class="table-cell">
                    {{ dateFormatter(org_invoice_payment.payment_at) }}
                  </td>
                  <td class="table-cell">
                    {{
                      currencyMoney(
                        org_invoice_payment.amount,
                        org_invoice.currency.symbol
                      )
                    }}
                  </td>
                  <td class="table-cell">
                    {{
                      currencyMoney(org_invoice_payment.exchange_rate, "S/.")
                    }}
                  </td>
                  <td class="table-cell">
                    {{
                      currencyMoney(
                        org_invoice_payment.interbank_commission,
                        org_invoice.currency.symbol
                      )
                    }}
                  </td>
                  <td class="table-cell-action">
                    <destroy-button
                      v-if="!org_invoice_payment.proc_button_destroy"
                      :disabled_button="false"
                      @destroyEvent="destroyInvoicePayment(org_invoice_payment)"
                      :label="$t('labels.destroy_button_label')"
                      :title="
                        $t('labels.destroy_org_invoice_payment_button_title')
                      "
                      :message="
                        $t('labels.destroy_org_invoice_payment_button_message')
                      "
                      :label_confirm="$t('labels.destroy_button_label_confirm')"
                    />
                    <destroy-button-processing
                      v-else
                      :label="$t('labels.destroy_button_label')"
                    />
                    <span class="space-actions"></span>
                    <edit-button
                      :label="$t('labels.edit_button')"
                      @click="goToEditInvoicePayment(org_invoice_payment)"
                    >
                    </edit-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-8 flex flex-row">
      <h1 class="page-title">
        <i class="fa-solid fa-money-check-dollar"></i>
        <span class="space-icon"></span>
        {{ $t("labels.title_org_invoice_discounts") }}
      </h1>
    </div>
    <div class="mt-8 flex flex-row justify-between">
      <div class="panel-plain-label">
        <badge-default
          :label="
            org_invoice_discounts.length +
            ' ' +
            $t('labels.total_number_org_invoice_discounts')
          "
        />
      </div>
      <new-button
        @click.prevent="goToNewInvoiceDiscount()"
        :label="$t('labels.new_org_invoice_discount_button')"
      />
    </div>
    <div class="mt-8 flex flex-col">
      <div class="table-first-container">
        <div class="table-second-container">
          <div class="table-third-container">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_discount.discount_code") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_discount.amount") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_discount.description") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_discount.exchange_rate") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_discount.discount_at") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.org_invoice_discount.discount_percent") }}
                  </th>
                  <th scope="col" class="table-header-cell"></th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr
                  class="table-row"
                  v-for="org_invoice_discount in org_invoice_discounts"
                  :key="org_invoice_discount.id"
                >
                  <td class="table-cell">
                    {{ org_invoice_discount.discount_code }}
                  </td>
                  <td class="table-cell">
                    {{
                      currencyMoney(
                        org_invoice_discount.amount,
                        org_invoice_discount.currency.symbol
                      )
                    }}
                  </td>
                  <td class="table-cell">
                    {{ org_invoice_discount.description }}
                  </td>
                  <td class="table-cell">
                    {{ org_invoice_discount.exchange_rate }}
                  </td>
                  <td class="table-cell">
                    {{ dateFormatter(org_invoice_discount.discount_at) }}
                  </td>
                  <td class="table-cell">
                    {{ org_invoice_discount.discount_percent }}
                  </td>
                  <td class="table-cell-action">
                    <destroy-button
                      v-if="!org_invoice_discount.proc_button_destroy"
                      :disabled_button="false"
                      @destroyEvent="
                        destroyInvoiceDiscount(org_invoice_discount)
                      "
                      :label="$t('labels.destroy_button_label')"
                      :title="
                        $t('labels.destroy_org_invoice_discount_button_title')
                      "
                      :message="
                        $t('labels.destroy_org_invoice_discount_button_message')
                      "
                      :label_confirm="$t('labels.destroy_button_label_confirm')"
                    />
                    <destroy-button-processing
                      v-else
                      :label="$t('labels.destroy_button_label')"
                    />
                    <span class="space-actions"></span>
                    <edit-button
                      :label="$t('labels.edit_button')"
                      @click="goToEditInvoiceDiscount(org_invoice_discount)"
                    >
                    </edit-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import Requester from "@/views/agro_exporter/requesters/finances/container_outcomes/show_org_invoice.js";
import ManageOrgInvoiceRequester from "@/views/agro_exporter/requesters/finances/container_outcomes/manage_org_invoice.js";
import ManageOrgInvoicePaymentRequester from "@/views/agro_exporter/requesters/finances/container_outcomes/manage_org_invoice_payment.js";
import ManageOrgInvoiceDiscountRequester from "@/views/agro_exporter/requesters/finances/container_outcomes/manage_org_invoice_discount.js";
import PayOrgInvoiceButton from "@/components/views/agro_exporter/finances/container_outcomes/PayOrgInvoiceButton.vue";
export default {
  components: { PayOrgInvoiceButton },
  name: "AeFinancesContainerOutcomesShowOrgInvoice",
  props: {
    agro_exporter_process_id: { required: true },
    agro_exporter_id: { required: true },
    org_invoice_id: { required: true },
    container_id: {}
  },
  data() {
    return {
      load_data_general: false,
      proc_save_button: false,
      proc_button_destroy: false,
      agro_exporter_process: {},
      org_invoice: {},
      org_invoice_payments: [],
      org_invoice_discounts: []
    };
  },
  computed: {
    total_amount_paid: function () {
      return this.org_invoice_payments.reduce(
        (sum, payment) => sum + payment.amount,
        0
      );
    }
  },
  methods: {
    loadData: async function () {
      await this.fetchAgroExporterProcess();
      await this.fetchOrgInvoice();
      await this.fetchOrgInvoicePayments();
      await this.fetchOrgInvoiceDiscounts();
      this.load_data_general = true;
    },
    fetchAgroExporterProcess: async function () {
      let response = await Requester.fetchAgroExporterProcess(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.agro_exporter_process = response.data;
    },
    fetchOrgInvoice: async function () {
      let response = await Requester.fetchOrgInvoice(
        this.agro_exporter_id,
        this.org_invoice_id
      );
      this.org_invoice = response.data;
    },
    fetchOrgInvoicePayments: async function () {
      let response = await Requester.fetchOrgInvoicePayments(
        this.agro_exporter_id,
        this.org_invoice_id
      );
      this.org_invoice_payments = response.data;
      this.org_invoice_payments.forEach(org_invoice_payment => {
        org_invoice_payment["proc_button_destroy"] = false;
      });
    },
    fetchOrgInvoiceDiscounts: async function () {
      let response = await Requester.fetchOrgInvoiceDiscounts(
        this.agro_exporter_id,
        this.org_invoice_id
      );
      this.org_invoice_discounts = response.data;
      this.org_invoice_discounts.forEach(org_invoice_discount => {
        org_invoice_discount["proc_button_destroy"] = false;
      });
    },
    updateExportInvoice: async function () {
      this.proc_save_button = true;
      try {
        let response = await ManageOrgInvoiceRequester.update(
          this.agro_exporter_id,
          this.org_invoice_id,
          {
            paid: !this.org_invoice.paid
          }
        );
        let org_invoice_update = response.data.org_invoice;
        this.org_invoice.paid = org_invoice_update.paid;
        this.org_invoice.amount_paid = org_invoice_update.amount_paid;
        this.registerSuccessNotification({
          title: response.data.success_message
        });
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.proc_save_button = false;
    },
    destroyExportInvoice: async function () {
      this.proc_button_destroy = true;
      try {
        let response = await ManageOrgInvoiceRequester.destroy(
          this.agro_exporter_id,
          this.org_invoice_id
        );
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowContainer();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.proc_button_destroy = false;
    },
    destroyInvoicePayment: async function (org_invoice_payment) {
      org_invoice_payment.proc_button_destroy = true;
      try {
        let response = await ManageOrgInvoicePaymentRequester.destroy(
          this.agro_exporter_id,
          org_invoice_payment.id
        );
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.org_invoice_payments.splice(
          this.org_invoice_payments.indexOf(org_invoice_payment),
          1
        );
        this.org_invoice.amount_paid = this.total_amount_paid;
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      org_invoice_payment.proc_button_destroy = false;
    },
    destroyInvoiceDiscount: async function (org_invoice_discount) {
      org_invoice_discount.proc_button_destroy = true;
      try {
        let response = await ManageOrgInvoiceDiscountRequester.destroy(
          this.agro_exporter_id,
          org_invoice_discount.id
        );
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.org_invoice_discounts.splice(
          this.org_invoice_discounts.indexOf(org_invoice_discount),
          1
        );
        this.org_invoice.amount_paid = this.total_amount_paid;
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      org_invoice_discount.proc_button_destroy = false;
    },
    goToEditExportInvoice: function () {
      this.$router.push({
        name: this.routeName("edit_org_invoice"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id
        }
      });
    },
    goToNewInvoicePayment: function () {
      this.$router.push({
        name: this.routeName("new_org_invoice_payment"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
          org_invoice_id: this.org_invoice_id
        }
      });
    },
    goToEditInvoicePayment: function (org_invoice_payment) {
      this.$router.push({
        name: this.routeName("edit_org_invoice_payment"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
          org_invoice_payment_id: org_invoice_payment.id
        }
      });
    },
    goToNewInvoiceDiscount: function () {
      this.$router.push({
        name: this.routeName("new_org_invoice_discount"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
          org_invoice_id: this.org_invoice_id
        }
      });
    },
    goToEditInvoiceDiscount: function (org_invoice_discount) {
      this.$router.push({
        name: this.routeName("edit_org_invoice_discount"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
          org_invoice_discount_id: org_invoice_discount.id
        }
      });
    },
    goToShowContainer: function () {
      this.$router.push({
        name: this.routeName("show_container"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
          container_id: this.container_id
        }
      });
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => this.loadData())
    );
  }
};
</script>
